import React from 'react';
import { Link } from 'react-router';
import type {
  CesiumAssetPartsFragment,
  FilePartsFragment,
  PicturePartsFragment,
  VomPartsFragment,
} from '~/apollo/generated/v3/graphql';
import { envVars } from '~/environment';
import { vomViewerRoute } from '~/paths';
import { isMini } from '~/utils/modules/vom';

// As described in: https://github.com/omt-tech/safari/issues/2109#issue-2225692875
// 1. If cesiumAsset does not exist, old link behaviour with old viewer
// 2. If VOM has cesiumAsset and no interpretations, goto new viewer
// 3. If VOM has cesiumAsset and has interpretations, main big div box link goes to new viewer and also display button (long rectangel? or similar) below div box with something like "Old viewer (with interpretations)" on it that goes to old viewer
// 4. (coming up soon, so plan for it) If vom has cesiumAsset and has interpretations and has <some to be defined cesium/vom interpretation data structure> only show goto new viewer
type UseCAViewerVom = Pick<VomPartsFragment, 'acquisitionType'> & {
  cesiumAsset?: Pick<CesiumAssetPartsFragment, 'approved'> | null;
};
export function isCesiumAssetReady(vom: UseCAViewerVom) {
  return vom.cesiumAsset?.approved;
}
export function shouldUseCesiumViewer(vom: UseCAViewerVom) {
  return !isMini(vom) && isCesiumAssetReady(vom);
}
export function shouldUseOptionalCesiumViewer(
  vom: UseCAViewerVom,
  interpreted: boolean,
) {
  return shouldUseCesiumViewer(vom) && interpreted;
}

type Props = {
  vom: Pick<VomPartsFragment, 'id' | 'v3GeoId' | 'outcropId'> & {
    cesiumAsset?: Pick<CesiumAssetPartsFragment, 'approved'> | null;
  };
  interpreted: boolean;
  picture?: PicturePartsFragment & {
    file: Pick<FilePartsFragment, 'signedUrl'>;
  };
  linkText?: React.ReactNode;
};

export function ExternalVOLink({
  vom,
  interpreted,
  picture,
  linkText = 'Click to start the virtual outcrop viewer',
}: Props) {
  const linkChildren = (
    <div>
      {picture && (
        <img
          src={picture.file.signedUrl}
          alt="Model thumbnail"
          className="w-full"
        />
      )}
      <div className="text-center text-sm p-1">{linkText}</div>
    </div>
  );

  if (vom.v3GeoId) {
    return <V3GeoLink v3GeoId={vom.v3GeoId}>{linkChildren}</V3GeoLink>;
  }

  if (shouldUseCesiumViewer(vom)) {
    return (
      <div>
        <CesiumLink outcropId={vom.outcropId} vomId={vom.id}>
          {linkChildren}
        </CesiumLink>
        {shouldUseOptionalCesiumViewer(vom, interpreted) && (
          <OldViewerLink vomId={vom.id} className="block link text-center">
            Old Viewer (with Interpretations)
          </OldViewerLink>
        )}
      </div>
    );
  }

  return (
    <OldViewerLink
      vomId={vom.id}
      className="block space-y-1 border-2 border-slate-200 hover:border-primary"
    >
      {linkChildren}
    </OldViewerLink>
  );
}

function CesiumLink({
  outcropId,
  vomId,
  children,
}: {
  outcropId: number;
  vomId: number;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={vomViewerRoute(outcropId, vomId)}
      className="block space-y-1 border-2 border-slate-200 hover:border-primary"
    >
      {children}
    </Link>
  );
}

function V3GeoLink({
  v3GeoId,
  children,
}: {
  v3GeoId: number;
  children: React.ReactNode;
}) {
  return (
    <a
      href={`https://v3geo.com/viewer/index.html#/${v3GeoId}`}
      target="_blank"
      rel="noreferrer"
      className="block space-y-1 border-2 border-slate-200 hover:border-primary"
    >
      {children}
    </a>
  );
}

function OldViewerLink({
  vomId,
  children,
  className,
}: {
  vomId: number;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <a
      href={`${envVars.VITE_CLIENT_URL}/vogviewer/index.html#/${vomId}`}
      target="_blank"
      rel="noreferrer"
      className={className}
    >
      {children ?? 'Old Viewer (with Interpretations)'}
    </a>
  );
}
